import { useQueries } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from './queryKeys';

/**
 * Deployed agents for MANY projects at once — { [projectId]: agents[] }.
 *
 * Backs the Projects list's per-row agent honeycomb. Two things make the
 * fan-out cheap rather than reckless:
 *
 *  1. It only asks for projects the server ALREADY said have agents. GET
 *     /projects returns `_counts.workflows` per row, so a project with none
 *     costs zero requests — the common "one empty project" account makes no
 *     extra calls at all.
 *  2. It reuses `queryKeys.agents.list(projectId)` — the SAME key the Pipelines
 *     page reads through useAgents. So the two share one cache entry: this
 *     warms the agent list a click before the user opens the project, and
 *     opening it doesn't refetch.
 *
 * Failures are swallowed per project (an errored row just renders no cluster) —
 * a decoration must never take a page down with it.
 */
export const useProjectAgents = (projects = []) => {
  const withAgents = projects.filter((p) => p?.projectId && Number(p?._counts?.workflows || 0) > 0);

  const results = useQueries({
    queries: withAgents.map((p) => ({
      queryKey: queryKeys.agents.list(p.projectId),
      queryFn: async () => {
        const data = await api.get(`/projects/${p.projectId}/workflows`);
        return Array.isArray(data) ? data : [];
      },
      // Longer than the Agents page's 10s: this is a glanceable decoration on a
      // list, not the surface where a just-deployed agent must appear. It also
      // means navigating back to Projects doesn't re-fan-out.
      staleTime: 60 * 1000,
      retry: false,
      // A list of clusters must never poll — the Agents page's own observer
      // owns the polling for the project the user actually opened.
      refetchInterval: false,
    })),
  });

  const byProject = {};
  withAgents.forEach((p, i) => {
    const data = results[i]?.data;
    if (Array.isArray(data) && data.length) byProject[p.projectId] = data;
  });
  return byProject;
};

export default useProjectAgents;
